import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";

interface DateRangeFilterProps {
  year: number | null;
  fromMonth: number | null;
  toMonth: number | null;
  onYearChange: (year: number) => void;
  onFromMonthChange: (month: number) => void;
  onToMonthChange: (month: number) => void;
}

const currentYear = new Date().getFullYear();

// Static year range: current year ± 5 years
const AVAILABLE_YEARS = Array.from({ length: 11 }, (_, i) => currentYear + 5 - i);

const MONTHS = [
  { value: 1, label: 'January', short: 'Jan' },
  { value: 2, label: 'February', short: 'Feb' },
  { value: 3, label: 'March', short: 'Mar' },
  { value: 4, label: 'April', short: 'Apr' },
  { value: 5, label: 'May', short: 'May' },
  { value: 6, label: 'June', short: 'Jun' },
  { value: 7, label: 'July', short: 'Jul' },
  { value: 8, label: 'August', short: 'Aug' },
  { value: 9, label: 'September', short: 'Sep' },
  { value: 10, label: 'October', short: 'Oct' },
  { value: 11, label: 'November', short: 'Nov' },
  { value: 12, label: 'December', short: 'Dec' },
];

export const DateRangeFilter = ({
  year,
  fromMonth,
  toMonth,
  onYearChange,
  onFromMonthChange,
  onToMonthChange,
}: DateRangeFilterProps) => {
  const isMobile = useIsMobile();

  const handleFromMonthChange = (month: number) => {
    onFromMonthChange(month);
    // Auto-adjust toMonth if it becomes invalid
    if (toMonth && month > toMonth) {
      onToMonthChange(month);
    }
  };

  const handleToMonthChange = (month: number) => {
    // Prevent setting toMonth before fromMonth
    if (fromMonth && month < fromMonth) {
      onToMonthChange(fromMonth);
    } else {
      onToMonthChange(month);
    }
  };

  if (isMobile) {
    return (
      <div className="grid grid-cols-3 gap-2 w-full">
        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Year</span>
          <Select
            value={year?.toString()}
            onValueChange={(value) => onYearChange(parseInt(value))}
          >
            <SelectTrigger className="h-12 w-full">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_YEARS.map(y => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">From</span>
          <Select
            value={fromMonth?.toString()}
            onValueChange={(value) => handleFromMonthChange(parseInt(value))}
          >
            <SelectTrigger className="h-12 w-full">
              <SelectValue placeholder="From" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {MONTHS.map(month => (
                <SelectItem key={month.value} value={month.value.toString()}>
                  {month.short}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">To</span>
          <Select
            value={toMonth?.toString()}
            onValueChange={(value) => handleToMonthChange(parseInt(value))}
          >
            <SelectTrigger className="h-12 w-full">
              <SelectValue placeholder="To" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {MONTHS.map(month => (
                <SelectItem key={month.value} value={month.value.toString()}>
                  {month.short}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <Label htmlFor="year-select">Year</Label>
        <Select
          value={year?.toString()}
          onValueChange={(value) => onYearChange(parseInt(value))}
        >
          <SelectTrigger id="year-select" className="w-[100px]">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {AVAILABLE_YEARS.map(y => (
              <SelectItem key={y} value={y.toString()}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Label htmlFor="from-month-select">From</Label>
        <Select
          value={fromMonth?.toString()}
          onValueChange={(value) => handleFromMonthChange(parseInt(value))}
        >
          <SelectTrigger id="from-month-select" className="w-[130px]">
            <SelectValue placeholder="From" />
          </SelectTrigger>
          <SelectContent className="max-h-[450px]">
            {MONTHS.map(month => (
              <SelectItem key={month.value} value={month.value.toString()}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Label htmlFor="to-month-select">To</Label>
        <Select
          value={toMonth?.toString()}
          onValueChange={(value) => handleToMonthChange(parseInt(value))}
        >
          <SelectTrigger id="to-month-select" className="w-[130px]">
            <SelectValue placeholder="To" />
          </SelectTrigger>
          <SelectContent className="max-h-[450px]">
            {MONTHS.map(month => (
              <SelectItem key={month.value} value={month.value.toString()}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
